import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useId, useMemo } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ExploreSearchParams } from "@/routes/explore/index";

function ClearIcon({ className, ...props }: React.ComponentProps<"svg">) {
  return (
    <svg
      aria-hidden="true"
      className={cn("size-[5px] text-black", className)}
      viewBox="0 0 6 6"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M2.301 2.999L.661 1.364A.507.507 0 01.667.669a.501.501 0 01.695-.005L2.999 2.3 4.637.663a.498.498 0 01.691.006c.193.19.193.508.006.695L3.7 2.999l1.634 1.637a.498.498 0 01-.005.694M2.3 3l2.992 2.296M2.301 3L.661 4.64a.503.503 0 00.006.692M2.301 3l-.94 2.337a.504.504 0 01-.694-.005m4.662 0l-.036-.036m.036.035l-.036-.035m.036.035a.498.498 0 01-.692.007L2.999 3.699m2.294 1.596a.448.448 0 01-.621.006L3.034 3.664 3 3.699m0 0l-.035-.035L1.326 5.3l.036.035L2.999 3.7zM.667 5.331l.035-.036-.035.036zM3 2.371l.035-.035h-.07L3 2.372z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.1"
      />
    </svg>
  );
}

interface FlashIconProps extends React.ComponentProps<"svg"> {
  colors: string[];
}

function FlashIcon({ className, colors, ...props }: FlashIconProps) {
  const id = useId();

  return (
    <svg
      aria-hidden="true"
      className={cn("h-4 w-3.5", className)}
      fill="none"
      viewBox="0 0 18 22"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g filter={`url(#${id}a)`}>
        <path
          clipRule="evenodd"
          d="M8.85 3.05l-.19.353a11582.208 11582.208 0 01-2.181 4.03 1657.557 1657.557 0 00-1.627 3.004l-.5.924c-.1.184.039.403.255.404l1.113.002a80.63 80.63 0 011.587.01c0 .005-.048.097-.107.205l-.19.35-.257.475A2178.496 2178.496 0 015.41 15.29a198.728 198.728 0 01-.587 1.084l-.75 1.387-.418.77-.25.46c-.017.028.034-.01.196-.147a468.02 468.02 0 011.352-1.13 2437.694 2437.694 0 012.954-2.472 1724.035 1724.035 0 001.292-1.08 864.54 864.54 0 002.386-1.996l.73-.61 1.303-1.09c.086-.072.033-.208-.08-.208l-1.258-.002a78.965 78.965 0 01-1.585-.011c0-.005.087-.169.193-.365a1047.038 1047.038 0 00.938-1.73 1990.036 1990.036 0 001.768-3.266l.34-.628.412-.76c.14-.259.255-.476.255-.483 0-.01-.84-.013-2.861-.013H8.936a.097.097 0 00-.085.05z"
          fill={`url(#${id}b)`}
          fillRule="evenodd"
        />
      </g>
      <defs>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${id}b`}
          x1="3.696"
          x2="18.771"
          y1="17.933"
          y2="7.154"
        >
          <stop stopColor={colors[0]} />
          <stop offset="1" stopColor={colors[1]} />
        </linearGradient>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="21.6"
          id={`${id}a`}
          width="16.799"
          x="0.6"
          y="0.2"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="1.4" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 0.929348 0 0 0 0 0.44144 0 0 0 0 0.524009 0 0 0 0.1 0" />
          <feBlend
            in2="BackgroundImageFix"
            result="effect1_dropShadow_18444_85718"
          />
          <feBlend
            in="SourceGraphic"
            in2="effect1_dropShadow_18444_85718"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
}

export default function BookingFilter() {
  const search = useSearch({ strict: false }) as ExploreSearchParams;
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const isActive = useMemo(() => !!search.has_bookings, [search.has_bookings]);

  const handleApply = () => {
    navigate({
      search: {
        ...search,
        has_bookings: "Yes",
      },
      to: pathname,
    });
  };

  const handleClear = () => {
    const { has_bookings: _, ...newSearch } = search;

    navigate({
      search: newSearch,
      to: pathname,
    });
  };

  const handleToggle = () => (isActive ? handleClear : handleApply)();

  const { buttonClassName, textClassName } = useMemo(() => {
    let buttonClassName = "";
    let textClassName = "";

    if (isActive) {
      buttonClassName = "bg-white";
      textClassName =
        "bg-clip-text bg-linear-44 from-tangerine from-5% to-bright-pink to-105% text-transparent";
    } else {
      buttonClassName =
        "bg-linear-44 from-tangerine from-5% to-bright-pink to-105%";
    }

    return { buttonClassName, textClassName };
  }, [isActive]);

  return (
    <Button
      className={cn(
        "relative flex h-7.5 items-center gap-1.5 rounded-[15px] pr-5 pl-3.5 text-white shadow-salmon-pink/40 transition-shadow hover:shadow-0-0-10",
        buttonClassName
      )}
      onClick={handleToggle}
    >
      <FlashIcon
        colors={isActive ? ["#ff9356", "#bd2885"] : ["#fff", "#fff"]}
      />
      <span className={cn("font-semibold text-12 text-white", textClassName)}>
        Book instantly
      </span>
      {isActive && (
        <span className="absolute -top-0.5 right-px flex size-2.5 items-center justify-center rounded-full bg-white shadow-0-0-4 shadow-black">
          <ClearIcon />
        </span>
      )}
    </Button>
  );
}
